import { useEffect, useRef, useState } from 'react';
import { useControllableState } from '../../core/utils/useControllableState.tsx';
import { Option } from '../../core/components/Fields/AutoCompleteField';

const useForceSelection = ({
    options = [],
    defaultValue,
    value: propsValue,
    onValueChange,
    forceSelection = false,
    resetInvalidOnBlur = false, // either reset value or clear it if invalid on blur
    onFocus,
    onBlur,
    onCommit,
    onSelectOption,
}: any) => {
    const [isInputFocused, setIsInputFocused] = useState<boolean>(false);

    const [value, setValue] = useControllableState(defaultValue, propsValue, onValueChange);
    const [inputValue, setInputValue] = useState<string>(value);
    // remember with what value we started on focus, so we can reset to it if needed
    const [valueWhenFocused, setValueWhenFocused] = useState<string>(value);

    const updateValue = (updateValue: string) => {
        if (forceSelection) {
            // Basically if force selection is active we keep the external value at '' unless valid input is entered
            const option = options.find((option) => option.value === updateValue);
            const optionWhenFocused = options.find((option) => option.value === valueWhenFocused);

            if (!option) {
                setValue(resetInvalidOnBlur ? valueWhenFocused : '');
                onSelectOption?.(resetInvalidOnBlur ? optionWhenFocused : null); // reset potential external listeners
            } else if (option && option.value !== valueWhenFocused) {
                // prevent unnecessary updates by checking if the value actually changed from when we opened the dropdown
                setValue(option.value);
                onSelectOption?.(option);
            }
        } else {
            setValue(updateValue);
        }
    };

    const updateInputValue = (value: string) => {
        setInputValue(value);
    };

    useEffect(() => {
        // only listen to external value changes when the input is not focused
        if (!isInputFocused) {
            updateInputValue(value);
        }
    }, [value]);

    useEffect(() => {
        // only update value while typing when the input is focused
        // skip if the user already made an explicit selection (inputValue === valueWhenFocused)
        // to prevent stale search results from clearing the lookup_id of the selection
        if (isInputFocused && inputValue !== valueWhenFocused) {
            updateValue(inputValue);
        }
    }, [options.map((option: Option) => option.value).join(',')]);

    const handleFocus = (e) => {
        // store previous value, so we can play around with the external value but still remember its original state
        setIsInputFocused(true);
        setValueWhenFocused(value);
        onFocus?.(e);
    };

    const justSelectedRef = useRef(false);

    // This hook owns value finalization, so it also owns the commits: the base field gets no
    // onCommit and stays silent, and every commit is fired here with the final value as a
    // local — no interception, no stale closures, and a blur deferred by a wrapper (e.g.
    // useMasterdataBrowser) simply commits whenever it actually runs.
    const handleBlur = (e) => {
        setIsInputFocused(false);

        let committedValue = inputValue;
        const justSelected = justSelectedRef.current;
        // reset input value to empty string if invalid value (e.g. user typed in a value that is not in the options)
        // skip when handleSelectOption just ran — its setState calls haven't flushed yet,
        // so `value`/`inputValue` are stale and the check would incorrectly reset to the old value
        if (forceSelection && !justSelected && value !== inputValue) {
            committedValue = resetInvalidOnBlur ? value : '';
            updateInputValue(committedValue);
        }
        justSelectedRef.current = false;

        onBlur?.(e);

        // an explicit selection already committed in handleSelectOption
        if (!justSelected) {
            onCommit?.(committedValue);
        }
    };

    const handleSelectOption = (option: Option) => {
        justSelectedRef.current = true;
        // on select we know it's always a valid option
        updateInputValue(option.value);
        setValue(option.value);
        setValueWhenFocused(option.value);
        // the callback may return commit data for this selection (e.g. lookup payload)
        const updatePayload = onSelectOption?.(option);
        onCommit?.(option.value, updatePayload);
    };

    const handleValueChange = (value: string) => {
        // typing after an explicit selection starts a new input that blur must handle again
        justSelectedRef.current = false;
        updateInputValue(value);
        updateValue(value);
    };

    return {
        value: inputValue,
        onValueChange: handleValueChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onSelectOption: handleSelectOption,
    };
};

export default useForceSelection;
